import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { cn, formatCentsWithCurrency } from '@/lib/utils'
import {
  getAvailableAddOnsApi,
  getMyAddOnsApi,
  purchaseAddOnApi,
  cancelAddOnApi,
  type AvailableAddOn,
  type OrgAddOn,
} from '@/lib/billing'
import {
  LoaderIcon,
  PlusCircleIcon,
  ShoppingCartIcon,
  XCircleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'

type Tab = 'available' | 'my'


function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

const BILLING_MODEL_LABELS: Record<string, string> = {
  monthly_recurring: 'Monthly',
  annual_recurring: 'Annually',
  one_time: 'One-time',
}

const BILLING_MODEL_COLORS: Record<string, string> = {
  monthly_recurring: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  annual_recurring: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  one_time: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
}

export const Route = createFileRoute('/dashboard/billing/add-ons')({
  component: () => {
    const [tab, setTab] = useState<Tab>('available')
    const [available, setAvailable] = useState<AvailableAddOn[]>([])
    const [myAddOns, setMyAddOns] = useState<OrgAddOn[]>([])
    const [loading, setLoading] = useState(true)
    const [purchasing, setPurchasing] = useState<string | null>(null)
    const [cancelling, setCancelling] = useState<number | null>(null)

    const load = async () => {
      setLoading(true)
      try {
        const [availRes, myRes] = await Promise.all([
          getAvailableAddOnsApi(),
          getMyAddOnsApi(),
        ])
        setAvailable(availRes.data)
        setMyAddOns(myRes.data)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load add-ons')
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => { load() }, [])

    const handlePurchase = async (addOnKey: string) => {
      setPurchasing(addOnKey)
      try {
        const result = await purchaseAddOnApi({ add_on_key: addOnKey })
        toast.success(`Purchased ${result.add_on.display_name}`)
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Purchase failed')
      } finally {
        setPurchasing(null)
      }
    }

    const handleCancel = async (orgAddOnId: number) => {
      setCancelling(orgAddOnId)
      try {
        await cancelAddOnApi(orgAddOnId)
        toast.success('Add-on cancelled')
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Cancellation failed')
      } finally {
        setCancelling(null)
      }
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
          <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
          <p className="ml-2 text-sm text-muted-foreground">Loading add-ons…</p>
        </div>
      )
    }

    const activeAddOns = myAddOns.filter(a => a.status === 'active')
    const cancelledAddOns = myAddOns.filter(a => a.status !== 'active')

    return (
      <div className="space-y-6">
        {/* Sub-tabs */}
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1 overflow-x-auto">
          {([
            { key: 'available' as Tab, label: 'Available', count: available.length },
            { key: 'my' as Tab, label: 'My add-ons', count: activeAddOns.length },
          ]).map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors whitespace-nowrap',
                tab === t.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
              {t.count > 0 && (
                <span className={cn(
                  'rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                  tab === t.key
                    ? 'bg-primary-foreground/20'
                    : 'bg-muted',
                )}>
                  {t.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Available Add-ons */}
        {tab === 'available' && (
          <>
            {available.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
                <CheckCircleIcon className="size-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">All add-ons purchased</p>
                <p className="text-sm text-muted-foreground">You've already purchased every available add-on.</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {available.map((addOn) => {
                  const isPurchasing = purchasing === addOn.add_on_key
                  return (
                    <div
                      key={addOn.add_on_id}
                      className="group rounded-xl border border-border bg-card p-5 transition-all hover:shadow-md hover:-translate-y-0.5"
                    >
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <PlusCircleIcon className="size-5" />
                        </div>
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${BILLING_MODEL_COLORS[addOn.billing_model]}`}>
                          {BILLING_MODEL_LABELS[addOn.billing_model]}
                        </span>
                      </div>

                      <h3 className="text-sm font-semibold text-foreground">{addOn.display_name}</h3>
                      {addOn.description && (
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{addOn.description}</p>
                      )}

                      {addOn.tier_required && (
                        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-muted-foreground">
                          <ShieldCheckIcon className="size-3" />
                          Requires {addOn.tier_required === 'enterprise' ? 'Enterprise' : 'Growth'} tier
                        </div>
                      )}

                      <div className="mt-4 flex items-center justify-between">
                        <p className="text-lg font-bold tabular-nums text-foreground">
                          {formatCentsWithCurrency(addOn.price_cents, 'EUR')}
                          <span className="text-xs font-normal text-muted-foreground">
                            {addOn.billing_model === 'monthly_recurring' ? '/mo' : addOn.billing_model === 'annual_recurring' ? '/yr' : ' once'}
                          </span>
                        </p>
                        <Button
                          size="sm"
                          onClick={() => handlePurchase(addOn.add_on_key)}
                          disabled={isPurchasing}
                          className="gap-1.5"
                        >
                          {isPurchasing ? (
                            <LoaderIcon className="size-3.5 animate-spin" />
                          ) : (
                            <ShoppingCartIcon className="size-3.5" />
                          )}
                          {isPurchasing ? 'Purchasing…' : 'Purchase'}
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}

        {/* My Add-ons */}
        {tab === 'my' && (
          <>
            {myAddOns.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
                <ShoppingCartIcon className="size-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No add-ons purchased</p>
                <p className="text-sm text-muted-foreground">Browse the Available tab to see what you can add.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Active */}
                {activeAddOns.length > 0 && (
                  <div>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                      Active ({activeAddOns.length})
                    </h3>
                    <div className="space-y-3">
                      {activeAddOns.map((addOn) => {
                        const isCancelling = cancelling === addOn.org_add_on_id
                        return (
                          <div
                            key={addOn.org_add_on_id}
                            className="flex items-start justify-between gap-4 rounded-xl border border-emerald-500/20 bg-card p-4"
                          >
                            <div className="flex items-start gap-3">
                              <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500">
                                <CheckCircleIcon className="size-4" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-foreground">{addOn.display_name}</p>
                                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${BILLING_MODEL_COLORS[addOn.billing_model]}`}>
                                    {BILLING_MODEL_LABELS[addOn.billing_model]}
                                  </span>
                                </div>
                                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                  <span>{formatCentsWithCurrency(addOn.unit_price_cents, 'EUR')}/{addOn.billing_model === 'monthly_recurring' ? 'mo' : addOn.billing_model === 'annual_recurring' ? 'yr' : 'once'}</span>
                                  {addOn.quantity > 1 && <span>×{addOn.quantity}</span>}
                                  <span>Since {formatDate(addOn.purchased_at)}</span>
                                </div>
                                {addOn.next_billing_date && (
                                  <p className="mt-0.5 text-[10px] text-muted-foreground/60">
                                    Next billing: {formatDate(addOn.next_billing_date)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancel(addOn.org_add_on_id)}
                              disabled={isCancelling}
                              className="shrink-0 gap-1.5 border-destructive/30 text-destructive hover:bg-destructive/10"
                            >
                              {isCancelling ? (
                                <LoaderIcon className="size-3.5 animate-spin" />
                              ) : (
                                <XCircleIcon className="size-3.5" />
                              )}
                              Cancel
                            </Button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                {/* Inactive (cancelled / expired) */}
                {cancelledAddOns.length > 0 && (
                  <div>
                    <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
                      Inactive ({cancelledAddOns.length})
                    </h3>
                    <div className="space-y-2">
                      {cancelledAddOns.map((addOn) => (
                        <div
                          key={addOn.org_add_on_id}
                          className="flex items-center gap-3 rounded-lg bg-muted/30 px-3 py-2 opacity-60"
                        >
                          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <XCircleIcon className="size-3.5" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-muted-foreground line-through">{addOn.display_name}</p>
                            <p className="text-[10px] text-muted-foreground/60">
                              {addOn.status === 'cancelled' ? 'Cancelled' : 'Expired'}
                              {addOn.cancelled_at && ` · ${formatDate(addOn.cancelled_at)}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    )
  },
})
